import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';

import {
  faC,
  faChartPie,
  faGraduationCap,
  faImage,
  faShapes,
  faSignOut,
  faUser,
  faWindowMaximize,
} from '@fortawesome/pro-light-svg-icons';
import { NavItemService } from '@frontend/util/nav-item-access';
import {
  NavItem,
  NavLink,
  NavPlatformData,
  SubNavEvent,
} from '@terminus-ui/nav';
import { AuthService, InstanceItem, TsAuthModule } from 'auth';
import { EntitlementsService } from 'entitlements';
import { EnvService } from 'frontend-environment';
import { GlobalPanelModule } from 'global-panel';
import { LakeFrontV2Module } from 'lake-front-v2';
import { PageWithNavComponent } from 'layouts';
import {
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';

export type MainLayoutEnv = {
  LEGACY_STUDIO_APP_URL: string;
};

@Component({
  selector: 'ds-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  providers: [],
  imports: [
    RouterModule,
    AsyncPipe,
    PageWithNavComponent,
    GlobalPanelModule,
    TsAuthModule,
    LakeFrontV2Module,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent {
  private envService = inject(EnvService);
  private authService = inject(AuthService);
  private navItemsService = inject(NavItemService);
  private entitlementService = inject(EntitlementsService);

  navData$ = this.navItemsService.filteredNavItems$;
  user$ = this.navItemsService.user$;
  appName$ = this.navItemsService.appName$;
  impersonatingUser$ = this.authService.isImpersonating$;

  private navItemData: NavPlatformData = [
    {
      title: 'Account Engagement Hub',
      items: [
        {
          icon: faShapes,
          text: 'Data Studio',
          path: '/data-studio',
          requireNotLimitedView: true,
          disabled: true,
        },
        {
          icon: faChartPie,
          text: 'Measurement Studio',
          path: '/measure-studio',
          requireNotLimitedView: true,
          disabled: true,
        },
        {
          icon: faC,
          text: 'Central',
          path: '/central',
          requireHubReportsAccess: 'app.central',
          id: 'central',
          requireNotLimitedView: true,
          disabled: true,
        },
      ],
    },
    {
      title: 'Channels',
      items: [
        {
          icon: faImage,
          text: 'Ad Experiences',
          path: '/adex',
          disabled: true,
          requireAdexMarketer: true,
        },
        {
          icon: faWindowMaximize,
          text: 'Web Experiences',
          path: this.envService.WEB_EXP_APP_URL,
          requireNotLimitedView: true,
          disabled: true,
        },
      ],
    },
  ];

  public async switchInstance(event: SubNavEvent) {
    this.authService
      .getAppInstancePair({
        instanceId: event.id,
        type: event.type,
      })
      .subscribe((pair) => {
        if (!pair || pair.length !== 2) {
          console.error('Invalid instance pair:', pair);
          return;
        }
        this.authService.switchAppInstance(pair[0], pair[1]);
      });
  }

  private updateNavData(
    instances: InstanceItem[] | null,
    filter: string,
    item: NavItem,
  ) {
    if (!instances) {
      return;
    }
    const filteredInstances = instances.filter((instance: InstanceItem) => {
      return instance.type === filter;
    });
    if (filteredInstances.length > 0) {
      item.items = filteredInstances.map((instance) => {
        return {
          ...instance,
          path: (item as NavLink)?.path,
        };
      });
    }
    item.items?.map((subNav) => {
      if (subNav) {
        subNav.onClick = (event: SubNavEvent) => {
          this.switchInstance(event).then(() => {
            if (event.path) {
              window.location.replace(event.path);
            }
          });
        };
      }
    });
  }

  navPlatformData$: Observable<NavPlatformData> =
    this.authService.getAppInstances$.pipe(
      withLatestFrom(of(this.navItemData)),
      map(([instances, navData]) => {
        this.updateNavData(
          instances as InstanceItem[] | null,
          'DATA_STUDIO',
          navData[0].items[0],
        );
        this.updateNavData(
          instances as InstanceItem[] | null,
          'DATA_STUDIO',
          navData[0].items[1],
        );
        this.updateNavData(
          instances as InstanceItem[] | null,
          'ADX',
          navData[1].items[0],
        );
        return navData;
      }),
      switchMap((navItemData) =>
        combineLatest(
          navItemData.map((section) =>
            combineLatest(
              section.items.map((item) =>
                this.entitlementService.getEntitlement$(item).pipe(
                  map((entitlement) => {
                    return {
                      ...item,
                      disabled: entitlement !== true,
                    };
                  }),
                ),
              ),
            ).pipe(map((items) => ({ ...section, items }))),
          ),
        ),
      ),
    );

  navProfileData = [
    [
      {
        icon: faUser,
        text: 'Profile',
        path: `/home/profile`,
      },
    ],
    [
      {
        icon: faGraduationCap,
        text: 'Platform Academy',
        path: 'https://customer-academy.terminus.com/app',
        newWindowOpen: true,
      },
    ],
    [{ icon: faSignOut, text: 'Sign Out', path: '/logout' }],
  ];
}
